/**
 * RFC 8785 JSON Canonicalization Scheme (JCS) and SHA-256 hashing.
 *
 * Isomorphic (no node: imports) so apps/web can verify evidence ids too.
 *
 * Rules implemented:
 * - object keys sorted by UTF-16 code units, no whitespace;
 * - strings serialized as ECMAScript JSON.stringify does (which is what
 *   RFC 8785 specifies: minimal escapes, lower-case \u00XX for controls);
 * - numbers serialized with ECMAScript Number.prototype.toString
 *   (JSON.stringify), -0 as 0; NaN/Infinity rejected;
 * - properties whose value is `undefined` are omitted (like JSON.stringify);
 *   `undefined`, functions, symbols and bigints anywhere else are rejected.
 * - `toJSON()` is honoured (so Date serializes as its ISO string).
 */
export class CanonicalJsonError extends Error {
  override name = 'CanonicalJsonError'
}

export function canonicalize(value: unknown): string {
  return ser(value, '$')
}

function ser(value: unknown, path: string): string {
  if (value === null) return 'null'
  if (typeof value === 'object' && typeof (value as { toJSON?: unknown }).toJSON === 'function') {
    return ser((value as { toJSON: () => unknown }).toJSON(), path)
  }
  switch (typeof value) {
    case 'boolean':
      return value ? 'true' : 'false'
    case 'number':
      if (!Number.isFinite(value)) throw new CanonicalJsonError(`non-finite number at ${path}`)
      return Object.is(value, -0) ? '0' : JSON.stringify(value)
    case 'string':
      return JSON.stringify(value)
    case 'object': {
      if (Array.isArray(value)) {
        return '[' + value.map((v, i) => {
          if (v === undefined) throw new CanonicalJsonError(`undefined array element at ${path}[${i}]`)
          return ser(v, `${path}[${i}]`)
        }).join(',') + ']'
      }
      const obj = value as Record<string, unknown>
      const keys = Object.keys(obj).filter((k) => obj[k] !== undefined).sort(compareUtf16)
      return '{' + keys.map((k) => JSON.stringify(k) + ':' + ser(obj[k], `${path}.${k}`)).join(',') + '}'
    }
    default:
      throw new CanonicalJsonError(`unsupported ${typeof value} at ${path}`)
  }
}

/** Compare by UTF-16 code units (JS default string ordering, spelled out). */
function compareUtf16(a: string, b: string): number {
  return a < b ? -1 : a > b ? 1 : 0
}

// ---------------------------------------------------------------------------
// SHA-256 (FIPS 180-4), synchronous and dependency-free.

const K = new Uint32Array([
  0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5, 0x3956c25b, 0x59f111f1, 0x923f82a4, 0xab1c5ed5, 0xd807aa98,
  0x12835b01, 0x243185be, 0x550c7dc3, 0x72be5d74, 0x80deb1fe, 0x9bdc06a7, 0xc19bf174, 0xe49b69c1, 0xefbe4786,
  0x0fc19dc6, 0x240ca1cc, 0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da, 0x983e5152, 0xa831c66d, 0xb00327c8,
  0xbf597fc7, 0xc6e00bf3, 0xd5a79147, 0x06ca6351, 0x14292967, 0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13,
  0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85, 0xa2bfe8a1, 0xa81a664b, 0xc24b8b70, 0xc76c51a3, 0xd192e819,
  0xd6990624, 0xf40e3585, 0x106aa070, 0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5, 0x391c0cb3, 0x4ed8aa4a,
  0x5b9cca4f, 0x682e6ff3, 0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208, 0x90befffa, 0xa4506ceb, 0xbef9a3f7,
  0xc67178f2,
])

export function sha256(data: Uint8Array): Uint8Array {
  const h = new Uint32Array([0x6a09e667, 0xbb67ae85, 0x3c6ef372, 0xa54ff53a, 0x510e527f, 0x9b05688c, 0x1f83d9ab, 0x5be0cd19])
  const bitLen = data.length * 8
  const padded = new Uint8Array(((data.length + 9 + 63) >> 6) << 6)
  padded.set(data)
  padded[data.length] = 0x80
  const dv = new DataView(padded.buffer)
  dv.setUint32(padded.length - 8, Math.floor(bitLen / 0x100000000))
  dv.setUint32(padded.length - 4, bitLen >>> 0)
  const w = new Uint32Array(64)
  for (let off = 0; off < padded.length; off += 64) {
    for (let i = 0; i < 16; i++) w[i] = dv.getUint32(off + i * 4)
    for (let i = 16; i < 64; i++) {
      const w15 = w[i - 15]!
      const w2 = w[i - 2]!
      const s0 = ((w15 >>> 7) | (w15 << 25)) ^ ((w15 >>> 18) | (w15 << 14)) ^ (w15 >>> 3)
      const s1 = ((w2 >>> 17) | (w2 << 15)) ^ ((w2 >>> 19) | (w2 << 13)) ^ (w2 >>> 10)
      w[i] = (w[i - 16]! + s0 + w[i - 7]! + s1) >>> 0
    }
    let a = h[0]!, b = h[1]!, c = h[2]!, d = h[3]!, e = h[4]!, f = h[5]!, g = h[6]!, hh = h[7]!
    for (let i = 0; i < 64; i++) {
      const S1 = ((e >>> 6) | (e << 26)) ^ ((e >>> 11) | (e << 21)) ^ ((e >>> 25) | (e << 7))
      const ch = (e & f) ^ (~e & g)
      const t1 = (hh + S1 + ch + K[i]! + w[i]!) >>> 0
      const S0 = ((a >>> 2) | (a << 30)) ^ ((a >>> 13) | (a << 19)) ^ ((a >>> 22) | (a << 10))
      const maj = (a & b) ^ (a & c) ^ (b & c)
      const t2 = (S0 + maj) >>> 0
      hh = g
      g = f
      f = e
      e = (d + t1) >>> 0
      d = c
      c = b
      b = a
      a = (t1 + t2) >>> 0
    }
    h[0] = (h[0]! + a) >>> 0
    h[1] = (h[1]! + b) >>> 0
    h[2] = (h[2]! + c) >>> 0
    h[3] = (h[3]! + d) >>> 0
    h[4] = (h[4]! + e) >>> 0
    h[5] = (h[5]! + f) >>> 0
    h[6] = (h[6]! + g) >>> 0
    h[7] = (h[7]! + hh) >>> 0
  }
  const out = new Uint8Array(32)
  const odv = new DataView(out.buffer)
  for (let i = 0; i < 8; i++) odv.setUint32(i * 4, h[i]!)
  return out
}

export function toHex(bytes: Uint8Array): string {
  let s = ''
  for (const b of bytes) s += b.toString(16).padStart(2, '0')
  return s
}

/** Lower-case hex SHA-256 of a string's UTF-8 bytes. */
export function sha256Hex(text: string): string {
  return toHex(sha256(new TextEncoder().encode(text)))
}

/** Lower-case hex SHA-256 of the canonical JSON of `value`. */
export function canonicalHash(value: unknown): string {
  return sha256Hex(canonicalize(value))
}

// ---------------------------------------------------------------------------
// Evidence ids (docs/DOMAIN.md evidence.objects.id)

export interface EvidenceIdInput {
  objectType: string
  schemaVersion: number
  actorKind: string
  actorId: string
  /** null or omitted when the object is not about an agent */
  agentId?: string | null | undefined
  idempotencyKey: string
  /** lineage, order preserved as given */
  sourceObjectIds?: readonly string[] | undefined
  payload: unknown
}

/** The exact object whose canonical JSON is hashed into the evidence id. */
export function evidenceIdPreimage(input: EvidenceIdInput) {
  return {
    object_type: input.objectType,
    schema_version: input.schemaVersion,
    actor_kind: input.actorKind,
    actor_id: input.actorId,
    agent_id: input.agentId ?? null,
    idempotency_key: input.idempotencyKey,
    source_object_ids: [...(input.sourceObjectIds ?? [])],
    payload: input.payload,
  }
}

/**
 * `id = hex(sha256(JCS({ object_type, schema_version, actor_kind, actor_id,
 * agent_id, idempotency_key, source_object_ids, payload })))`.
 * `agent_id` is null when absent; `source_object_ids` is `[]` when absent.
 */
export function evidenceId(input: EvidenceIdInput): string {
  return canonicalHash(evidenceIdPreimage(input))
}
